/**
 * Dashboard Service
 * Centraliza toda a lógica de negócio e processamento de dados do dashboard
 * 
 * IMPORTANTE: Para lógica de segmentação RFM, use o módulo rfmSegmentation.js
 * Este arquivo re-exporta as funções de segmentação para compatibilidade
 */

// Re-exportar funções de segmentação RFM do módulo dedicado
export {
  determinarSegmento,
  getSegmentClass,
  getSegmentDisplayName,
  getSegmentLabel,
  getSegmentBadgeVariant,
  getSegmentColorVariant,
  getBadgeRecencia,
  normalizarSegmento,
  processarMatrizRFM,
  calcularTotaisPorSegmento,
  getClientesPorSegmento,
  getCellClass,
  RFM_SEGMENTS,
  SEGMENTOS,
} from './rfmSegmentation'

// Alias para compatibilidade com código existente
export { determinarSegmento as determinarSegmentoAPI } from './rfmSegmentation'

/**
 * Formata valores monetários
 */
export const formatarMoeda = valor => {
  if (!valor) return '0,00'
  return Number(valor).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

/**
 * Formata tempo em minutos para horas e minutos
 */
export const formatarTempo = minutos => {
  const horas = Math.floor(minutos / 60)
  const mins = minutos % 60
  return `${horas}h ${mins}m`
}

/**
 * Processa matriz RFM a partir dos dados da API (formato novo)
 * @param {Object} dadosAPI - Objeto data.matriz_rfm da resposta da API
 * @param {Array} clientes - Array de clientes com rfm_scores e segmento
 * @returns {Object} Matriz RFM processada no formato esperado pelo componente
 * @deprecated Use processarMatrizRFM de rfmSegmentation.js
 */
export const processarMatrizRFMNova = (dadosAPI, clientes = []) => {
  // Importar a função correta do módulo de segmentação
  const { processarMatrizRFM, determinarSegmento } = require('./rfmSegmentation')

  // Usar a nova implementação
  const dados = {
    matriz_rfm: dadosAPI,
    clientes,
  }

  return processarMatrizRFM(dados)
}

/**
 * Processa segmentos do backend e normaliza os nomes
 * @param {Object} segmentos - Objeto de segmentos do backend
 * @returns {Object} Segmentos normalizados
 */
export const processarSegmentos = segmentos => {
  if (!segmentos || typeof segmentos !== 'object') return {}

  const { normalizarSegmento } = require('./rfmSegmentation')
  const segmentosNormalizados = {}

  Object.entries(segmentos).forEach(([nome, total]) => {
    const nomeNormalizado = normalizarSegmento(nome)
    segmentosNormalizados[nomeNormalizado] = total
  })

  return segmentosNormalizados
}

/**
 * Processa dados de UTMs
 */
export const processarUTMs = infos => {
  if (!infos || !Array.isArray(infos)) return []

  const utmMap = {}

  infos.forEach(info => {
    if (!info.utm_source) return

    const source = info.utm_source
    if (!utmMap[source]) {
      utmMap[source] = {
        utm_source: source,
        count: 0,
        utm_campaigns: [],
      }
    }

    utmMap[source].count++

    if (info.utm_campaign) {
      const campaignIndex = findCampaignIndex(utmMap[source].utm_campaigns, info.utm_campaign)

      if (campaignIndex === -1) {
        utmMap[source].utm_campaigns.push({
          name: info.utm_campaign,
          total: 1,
          amount: parseFloat(info.transaction_amount || 0),
        })
      } else {
        utmMap[source].utm_campaigns[campaignIndex].total++
        utmMap[source].utm_campaigns[campaignIndex].amount += parseFloat(info.transaction_amount || 0)
      }
    }
  })

  return Object.values(utmMap)
}

/**
 * Processa dados de Cupons
 */
export const processarCupons = transactions => {
  if (!transactions || !Array.isArray(transactions)) return []

  const couponMap = {}

  transactions.forEach(transaction => {
    if (!transaction.coupon) return

    const { coupon } = transaction
    if (!couponMap[coupon]) {
      couponMap[coupon] = {
        name: coupon,
        total: 0,
        amount: 0,
      }
    }

    couponMap[coupon].total++
    couponMap[coupon].amount += parseFloat(transaction.amount || 0)
  })

  return Object.values(couponMap).sort((a, b) => b.total - a.total)
}

/**
 * Encontra índice de campanha no array
 */
const findCampaignIndex = (array, name) => array.findIndex(item => item.name === name)

/**
 * Retorna variante de status de campanha
 */
export const getStatusVariantCampanha = status => {
  const variantMap = {
    ativo: 'success',
    pausado: 'warning',
    concluido: 'secondary',
    rascunho: 'info',
  }
  return variantMap[status?.toLowerCase()] || 'secondary'
}

/**
 * Retorna label de status de campanha
 */
export const getStatusLabelCampanha = status => {
  const labelMap = {
    ativo: 'Ativo',
    pausado: 'Pausado',
    concluido: 'Concluído',
    rascunho: 'Rascunho',
  }
  return labelMap[status?.toLowerCase()] || status
}
